import {
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Grid,
  Box,
  IconButton,
  CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { createStyles } from '@material-ui/core/styles';
import {
  ImageCarouselViewer,
  PaperBorderType,
  multiPageScreenshotGrid,
  StandardDialog,
  Tabs,
  PhotoGalleryModal,
  Tooltip,
} from '@roc/ui';
import { useParams } from 'react-router';
import { GetApp, Share, Edit, Clear, Remove, ArrowForward } from '@material-ui/icons';
import { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import { Paper } from '@roc/ui';
import { formatCurrency, formatPercentage } from '@roc/ui/utils';
import DrawReviewReportCategoryTable from './drawReviewReportCategoryTable';
import CopyUrlModal from '../../drawRequests/components/copyUrlModal';
import { formatPrecision } from '@roc/feature-utils';
import { DrawReviewReportBaseStore } from '../stores/drawReviewReportBaseStore';
import { useDrawsStore } from '../../hooks';
import { useDocumentStore } from '@roc/feature-documents';
import { InspectionReport } from '@roc/feature-inspections';
import { CropFree } from '@material-ui/icons';
import clsx from 'clsx';


const defaultAmount = '$0.00';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      marginBottom: 8,
    },
    topHeaderSection: {
      marginBottom: 16,
      fontFamily: 'Oswald, sans-serif;',
      fontWeight: 'bold',
    },
    loanValuesItem: {
      paddingRight: 24,
    },
    paper: {
      padding: theme.spacing(3, 0, 4),
      boxShadow: 'none',
    },
    headerGrid: {
      padding: '12px 48px 12px 48px',
    },
    leftHeaderData: {
      display: 'flex',
      flexDirection: 'row',
    },
    drawNumber: {
      paddingLeft: 24,
    },
    title: {
      color: '#333333',
      fontSize: 20,
      fontWeight: 'bold',
      fontFamily: 'Oswald, sans-serif;',
      marginBottom: 10,
      marginTop: 25,
    },
    table: {
      minWidth: 750,
      border: '2px #e0e0e0 solid',
      '& td': {
        height: '40px',
        padding: 0,
        paddingTop: '8px',
        paddingBottom: '8px',
      },
      '& td:first-child': {
        height: '40px',
        paddingLeft: '16px',
        paddingTop: '8px',
        paddingBottom: '8px',
      },
      '& th:first-child, td[rowSpan]': {
        paddingLeft: theme.spacing(2),
        paddingRight: '3px',
      },
      '& table tr:last-child td, & table td[rowSpan]': {
        borderBottom: 'none',
      },
    },
    headerCell: {
      height: '41px',
      fontWeight: 'bold',
      padding: '0 4px 0 0',
      fontFamily: 'Oswald, sans-serif;',
    },
    totalRow: {
      backgroundColor: '#FDEFBF',
    },
    shareDownloadContainer: {
      paddingRight: 24,
      display: 'flex',
      justifyContent: 'flex-end',
    },
    buttonContainer: {
      marginTop: 32,
    },
    container: {
      padding: 36,
    },
    downloadContainer: {
      display: 'flex',
      flexDirection: 'row',
      cursor: 'pointer',
      marginLeft: '17px',
    },
    buttonLabel: {
      fontSize: '14px',
      paddingLeft: '5px',
    },
    icon: {
      fontSize: '24px',
    },
    editContainter: {
      display: 'flex',
      flexDirection: 'row',
      cursor: 'pointer',
      marginLeft: '10px',
      marginTop: '8px',
    },
    info: {
      fontFamily: 'Oswald, sans-serif;',
      fontWeight: 'bold',
    },
    infoTotals: {
      fontFamily: 'Oswald, sans-serif;',
      fontWeight: 'bolder',
    },
    drawType: {
      textTransform: 'capitalize',
    },
    projectTotalTitlesColor: {
      backgroundColor: '#f8f6f8',
    },
    fontColor: {
      color: '#6b6a6b',
      marginLeft: '5%',
    },
    calculationCellsSize: {
      width: '37%',
    },
    marginArrow: {
      marginLeft: '32px',
      color: '#aeafb0',
    },
    declineButton: {
      borderColor: '#E2A529',
      borderWidth: '2px',
      backgroundColor: 'white',
    },
    saveButton: {
      backgroundColor: '#FDEFBF',
      borderColor: '#E2A529',
      borderWidth: '2px',
    },
    approveButton: {
      backgroundColor: '#F2BC16',
      borderColor: '#F2BC16',
      borderWidth: '2px',
    },
    totalSymbolsColor: {
      color: '#FDEFBF',
    },
    asteriskAlert: {
      marginTop: '20px',
    },
    titleDrawResults: {
      fontSize: 22,
      fontWeight: 'bold',
      fontFamily: 'Oswald, sans-serif;',
      marginBottom: 20,
      marginTop: 25,
    },
    alignRigth: {
      alignContent: 'right',
    },
    hiddenTitle: {
      fontSize: 22,
      fontWeight: 'bold',
      fontFamily: 'Oswald, sans-serif;',
      marginBottom: 20,
      marginTop: 25,
      color: '#ffffff',
    },
    dialog: {
      '& .MuiDialogContent-root': {
        height: 'calc(90vh - 64px)',
      },
    },
  })
);

interface DrawReviewReportSharedProps {
  store: DrawReviewReportBaseStore;
  isInternal: boolean;
  drawId?: number;
}

export const DrawReviewReportShared = observer((props: DrawReviewReportSharedProps) => {
  const { store, isInternal } = props;
  const classes = useStyles();
  const params = useParams<{ drawId?: string }>();
  const drawId = params.drawId ?? props.drawId;
  const [isCopyUrlModalOpen, setIsCopyUrlModalOpen] = useState(false);
  const { inspectionLink } = store;
  const { drawStore } = useDrawsStore();
  const { documentStore: { documentFormStore } } = useDocumentStore();
  const inspectionId = store.inspectionId;
  const [showPicturesModal, setShowPicturesModal] = useState(false);
  const [selectedPhotoKey, setSelectedPhotoKey] = useState(null);
  const arePicturesLoading = store?.arePicturesLoading || false;

  useEffect(() => {
    store.reset();
    store.loadTableData(props.drawId);
  }, []);


  const handleShareClick = () => {
    setIsCopyUrlModalOpen(true);
  };

  const handleCopyUrlModalClose = () => setIsCopyUrlModalOpen(false);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(inspectionLink);
    handleCopyUrlModalClose();
  };

  const onClose = async () => {
    drawStore.fetchDrawDTOById(`${drawId}`);
    documentFormStore.closeCurrentForm();
  }

  const handleApprove = async () => {
    // TODO: add submit
    await store.approveRequest();
    onClose();
  };

  const handleSave = async () => {
    // TODO: add save
    await store.saveReview();
    onClose();
  }

  const handleDecline = async () => {
    // TODO: add decline
    await store.declineRequest();
    onClose();
  };

  const handleDownload = () => {
    const screenshotsIds = ['report', 'draw-results'];
    if (store?.pictures?.length) {
      screenshotsIds.push('images-report');
    }
    multiPageScreenshotGrid('Draw_Review_Report.pdf', screenshotsIds, store.getIgnoredCompletePdfList());
  };

  const handleDownloadForExternalInspections = () => {
    const screenshotsIds = ['report', 'draw-results'];
    if (store?.pictures?.length) {
      screenshotsIds.push('images-report');
    }
    multiPageScreenshotGrid('Draw_Review_For_External_Inspections_Report.pdf', screenshotsIds, store.getIgnoredList());
  };

  const getAmount = (amount: number, isPercentage: boolean) => {
    if (isPercentage) {
      return amount ? `${formatPrecision(amount, 2)}%` : '0.00%';
    } else {
      return amount ? `${formatCurrency(amount)}` : defaultAmount;
    }
  }

  const getItemForDrawResults = (text: string[], amount: number[], isPercentage: boolean, notShowDivider?: boolean) => {
    return (
      <Grid item sm={12}>
        <Grid container direction='row' md={12}>
          <Grid item md={9}>
            <Typography variant='body1'>
              {text[0]} {<span style={{ color: '#6b6a6b' }}>{text.length > 1 ? text[1] : ''}</span>}
            </Typography>
          </Grid>
          <Grid item md={3}>
            <Typography align='right'>
              {amount.length === 1 ? getAmount(amount[0], isPercentage) : `${formatPrecision(amount[0], 0)}% | ${formatCurrency(amount[1], 2)}`}
            </Typography>
          </Grid>
        </Grid>
        {!notShowDivider && <Divider />}
      </Grid>
    )
  }

  const getCalculationItem = (text: string[], amount: number[], description: string, isPercentage: boolean) => {
    return (
      <Grid item sm={12}>
        <Grid container direction='row'>
          <Grid item md={6}>
            {getItemForDrawResults(text, amount, isPercentage, true)}
          </Grid>
          <Grid item md={1} alignContent='center'>
            <ArrowForward className={classes.marginArrow} />
          </Grid>
          <Grid item md={5}>
            <Typography variant='body1'>
              {<span style={{ color: '#6b6a6b' }}> {description} </span>}
            </Typography>
          </Grid>
        </Grid>
        <Divider />
      </Grid>
    )
  }

  const loanTermsColumns = [
    {
      text: ['Completed Renovations', '(As Stated by the Borrower. Work Already Installed & Paid For)'],
      amount: [store.capex.percentage, store.capex.amount],
      isPercentage: false
    },
    {
      text: ['Renovation Budget', '(Work Required to Complete the Project)'],
      amount: [store?.loanValues?.renoBudget],
      isPercentage: false
    },
    {
      text: ['Total Renovation Budget', '(Renovation Budget + Completed Renovations)'],
      amount: [store.totalRenoBudgetWithCapex],
      isPercentage: false
    },
    {
      text: ['Construction Holdback Rate', '(Renovation Budget Reimbursement Rate)'],
      amount: [store?.loanValues?.constructionHoldbackRate],
      isPercentage: true
    },
    {
      text: ['Construction Holdback', '(Amount Agreed to Borrower)'],
      amount: [store.loanValues?.constructionHoldback],
      isPercentage: false
    }
  ]

  const assestmentReviewSideDataColumns = [
    {
      text: ['Verified Completed Renovations'],
      amount: [store.capex.amount],
      isPercentage: false
    },
    {
      text: ['Verified Completed Renovation Budget'],
      amount: [store.verifiedCompletedRenovationBudget],
      isPercentage: false
    }
  ]

  const calculationsColumns = [
    {
      text: ['Total Renovation Budget Completed', '(Verified)'],
      amount: [(store.projectCompletion.percentage * 100), store.projectCompletion.amount],
      description: 'Verified Completed Renovations + Verified Completed Renovation Budget',
      isPercentage: false
    },
    {
      text: ['Verified Completed Renovations'],
      amount: [store.capex.amount],
      description: 'Verified Completed Renovations',
      isPercentage: false
    },
    {
      text: ['Renovation Budget Earned', '(Including This Draw)'],
      amount: [store.workCompletedSinceFunding.amount],
      description: 'Total Renovation Budget Completed (Verified) - Verified Completed Renovations',
      isPercentage: false
    },
    {
      text: ['Construction Holdback Earned'],
      amount: [store.totalProjectWire.amount],
      description: 'Renovation Budget Earned * Construction Holdback Rate / 100',
      isPercentage: false
    },
    {
      text: ['Construction Holdback Previously Disbursed'],
      amount: [store.loanValues?.contructionHoldbackReimbursed],
      description: 'Sum Gross Draw Wires Disbursed',
      isPercentage: false
    },
    {
      text: ['Gross Reimbursement This Draw'],
      amount: [store.grossWire],
      description: 'Construction Holdback Earned - Construction Holdback Previously Disbursed',
      isPercentage: false
    },
    {
      text: ['Fees'],
      amount: [store.fees],
      description: 'Inspection & Processing',
      isPercentage: false
    },
    {
      text: ['Net Wire This Draw'],
      amount: [store.netWire],
      description: 'Gross Reimbursement This Draw - Fees',
      isPercentage: false
    },
    {
      text: ['Construction Holdback Remaining'],
      amount: [store.constructionHoldbackRemainingCalculated],
      description: 'Construction Holdback - Construction Holdback Earned',
      isPercentage: false
    }
  ]

  const displayInspectionPhotos = !!(store?.pictures && store?.pictures?.length > 0 && store?.pictures[0]?.inspectionPhotoId);

  let inspectionPhotos = [];

  if (displayInspectionPhotos) {
    inspectionPhotos = store?.pictures
      .filter(picture => picture.reported !== true)
      .map(picture => ({
        key: picture.inspectionPhotoId,
        url: picture.image,
        preferredRotation: picture.preferredRotation,
        caption: picture.caption
      }))
  }

  const onPhotoRotationHandler = (photoKey, rotation) => {
    store?.setPhotoPreferredRotation(photoKey, rotation);
  }

  return (
    <Grid container spacing={2} className={classes.container}>
      <CopyUrlModal
        handleClose={handleCopyUrlModalClose}
        handleCopy={handleCopyUrl}
        isOpen={isCopyUrlModalOpen}
        url={inspectionLink}
      />
      <Typography variant='h3' className={classes.topHeaderSection}>
        {isInternal ? `Draw Review Report` : `Draw Report`}
      </Typography>
      <Grid item xs={12} className={classes.header}>
        <div className={classes.shareDownloadContainer}>
          {isInternal && (
            <div
              className={classes.downloadContainer}
              onClick={handleShareClick}
            >
              <Share className={classes.icon} />
              <p className={classes.buttonLabel}>INSPECTION LINK</p>
            </div>
          )}
          <DownloadBtn
            loading={arePicturesLoading}
            loadingTooltip="Please wait, photos are loading."
            text="DOWNLOAD AS PDF"
            onClick={handleDownload}
          />
          <DownloadBtn
            loading={arePicturesLoading}
            loadingTooltip="Please wait, photos are loading."
            text="DOWNLOAD PDF FOR EXTERNAL INSPECTIONS"
            onClick={handleDownloadForExternalInspections}
          />
          {arePicturesLoading && (
            <Box display="flex" alignItems="center" justifyContent="flex-end" pl={4}>
              <CircularProgress color="primary" style={{ width: '20px', height: '20px' }} />
              <Box pl={1}>Loading photos...</Box>
            </Box>
          )}
        </div>
      </Grid>
      <Grid
        item
        container
        spacing={2}
        id='report'
      >
        <Grid item xs={12} md={12}>
          <Paper className={classes.paper} borderType={PaperBorderType.NO_BORDER}>
            <Grid container spacing={2} className={classes.headerGrid}>
              <Typography variant='h4' className={classes.topHeaderSection}>
                Draw Details
              </Typography>
              <Grid container spacing={4} className={classes.topHeaderSection}>
                <Grid item xs={12} md={3}>
                  <Typography variant='body1'>
                    Project Address
                  </Typography>
                  <Divider />
                  <Typography variant='h6' className={classes.info}>
                    {store?.drawDetails?.projectAddress}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2} >
                  <Typography variant='body1'>
                    Borrower
                  </Typography>
                  <Divider />
                  <Typography variant='h5' className={classes.info}>
                    {store?.drawDetails?.borrowerName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant='body1'>
                    Lender
                  </Typography>
                  <Divider />
                  <Typography variant='h5' className={classes.info}>
                    {store?.drawDetails?.lenderName}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={1}>
                  <Typography variant='body1'>
                    Loan ID
                  </Typography>
                  <Divider />
                  <Typography variant='h5' className={classes.info}>
                    {store?.drawDetails?.loanId}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant='body1'>
                    Draw Number
                  </Typography>
                  <Divider />
                  <Typography variant='h5' className={classes.info}>
                    {`#${store?.drawDetails?.drawNumber}`}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Typography variant='body1'>
                    Draw Type
                  </Typography>
                  <Divider />
                  <Typography variant='h5' className={`${classes.info} ${classes.drawType}`}>
                    {store?.drawDetails?.drawType}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid container spacing={2} className={classes.headerGrid}>
              <Typography variant='h4' className={classes.topHeaderSection}>
                Disbursement Summary
              </Typography>
              <Grid container spacing={4} className={classes.topHeaderSection}>
                <Grid item xs={12} md={3}>
                  <Typography variant='body1'>
                    Total Renovation Budget Completed
                  </Typography>
                  <Divider />
                  <Typography variant='h5' className={classes.info}>
                    {`${formatPrecision(store?.totalRenoBudgetCompleted, 2)}%`}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant='body1'>
                    Construction Holdback Disbursed
                  </Typography>
                  <Divider />
                  <Typography variant='h5' className={classes.info}>
                    {store?.constructionHoldbackDisbursedIncludingThisDraw ? formatCurrency(store?.constructionHoldbackDisbursedIncludingThisDraw, 2) : defaultAmount}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant='body1'>
                    Construction Holdback Remaining
                  </Typography>
                  <Divider />
                  <Typography variant='h5' className={classes.info}>
                    {store?.constructionHoldbackRemainingIncludingThisDraw ? formatCurrency(store?.constructionHoldbackRemainingIncludingThisDraw, 2) : defaultAmount}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant='body1'>
                    Net Wire This Draw
                  </Typography>
                  <Divider />
                  <Typography variant='h5' className={classes.info}>
                    {`${store.netWire ? formatCurrency(store.netWire, 2) : defaultAmount}`}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid item xs={12} md={12}>
          <Paper className={classes.paper} borderType={PaperBorderType.NO_BORDER}>
            <Grid container spacing={2} className={classes.headerGrid}>
              <Grid container item xs={12} md={12}>
                <Typography variant='h4' className={classes.topHeaderSection}>
                  Draw Review
                </Typography>
                {isInternal &&
                  <div className={classes.editContainter} onClick={() => store.switchEdition()} data-html2canvas-ignore>
                    <Edit className={classes.icon} />
                  </div>
                }
              </Grid>
              {
                store && store.tableData && store.tableData.map(category => {
                  if (category.subCategories.length) {
                    return (<DrawReviewReportCategoryTable
                      category={category}
                      store={store}
                      key={category.name}
                      isInternal={isInternal}
                    />);
                  }
                })
              }
              <Grid item xs={12} md={12}>
                <Table className={classes.table}>
                  <TableHead className={classes.projectTotalTitlesColor}>
                    <TableRow>
                      <TableCell align='center'>
                        {(store.isProjectTotalEqualToReno ? '' : 'TOTAL ') + 'RENOVATION BUDGET'}
                      </TableCell>
                      <TableCell align='center'>
                        COMPLETED RENOVATION BUDGET {<span style={{ fontSize: '20px' }}> &nbsp;* </span>}
                      </TableCell>
                      <TableCell>
                        <Clear fontSize='small' />
                      </TableCell>
                      <TableCell align='center'>
                        CONSTRUCTION HOLDBACK RATE
                      </TableCell>
                      <TableCell align='center'>
                        <span style={{ fontSize: '20px', fontWeight: 'bold' }}> = </span>
                      </TableCell>
                      <TableCell align='center'>
                        CONSTRUCTION HOLDBACK COMPLETED
                      </TableCell>
                      <TableCell>
                        <Remove fontSize='small' />
                      </TableCell>
                      <TableCell align='center'>
                        CONSTRUCTION HOLDBACK PREVIOUSLY DISBURSED
                      </TableCell>
                      <TableCell>
                        <span style={{ fontSize: '20px', fontWeight: 'bold' }}> = </span>
                      </TableCell>
                      <TableCell align='center'>
                        GROSS DISBURSEMENT THIS DRAW
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow className={classes.totalRow}>
                      <TableCell align='center'>
                        <Typography variant='h6' className={classes.infoTotals}>
                          {store?.totalCalculatedRenoBudget ? formatCurrency(store.totalCalculatedRenoBudget, 2) : defaultAmount}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Typography variant='h6' className={classes.infoTotals}>
                          {`${formatPrecision((store?.workCompletedSinceFunding.percentage), 2)}% | ${formatCurrency(store.workCompletedSinceFunding.amount, 2)}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Clear className={classes.totalSymbolsColor} />
                      </TableCell>
                      <TableCell align='center'>
                        <Typography variant='h6' className={classes.infoTotals}>
                          {`${formatPercentage(store?.loanValues?.constructionHoldbackRate, 2)}`}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <span className={classes.totalSymbolsColor}> = </span>
                      </TableCell>
                      <TableCell align='center'>
                        <Typography variant='h6' className={classes.infoTotals}>
                          {`${formatPrecision(store.totalProjectWire.percentage, 2)}% | ${formatCurrency(store.totalProjectWire.amount, 2)}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Remove className={classes.totalSymbolsColor} />
                      </TableCell>
                      <TableCell id={'total_disbursement'}>
                        <Typography variant='h6' align='center' className={classes.infoTotals}>
                          {`${formatPrecision(((store.loanValues?.contructionHoldbackReimbursed / store.loanValues.constructionHoldback) * 100), 2)}% | ${formatCurrency(store.loanValues?.contructionHoldbackReimbursed, 2)}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <span className={classes.totalSymbolsColor}> = </span>
                      </TableCell>
                      <TableCell id={'total_disbursement'}>
                        <Typography variant='h6' align='center' className={classes.infoTotals}>
                          {`${formatCurrency(store.grossWire, 2)}`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
                {<Grid className={classes.asteriskAlert}><span style={{ fontSize: '20px' }}> *&nbsp;</span> <span> AS ASSESED INCLUDING THIS DRAW </span></Grid>}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Grid
        item
        xs={12}
        md={12}
        id='draw-results'
      >
        <Paper className={classes.paper} borderType={PaperBorderType.NO_BORDER}>
          <Grid container className={classes.headerGrid} >
            <Typography variant='h4' className={classes.topHeaderSection}>
              Draw Results
            </Typography>
            <Grid container justifyContent='space-between' spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant='h5' className={classes.titleDrawResults}>
                  Loan Terms
                </Typography>
                <Grid container spacing={4} >
                  {loanTermsColumns.map(column => {
                    return getItemForDrawResults(column.text, column.amount, column.isPercentage);
                  })}
                </Grid>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography variant='h5' className={classes.titleDrawResults}>
                  Assestment Review of Side Data
                </Typography>
                <Grid container spacing={4}>
                  {assestmentReviewSideDataColumns.map(column => {
                    return getItemForDrawResults(column.text, column.amount, column.isPercentage);
                  })}
                </Grid>
              </Grid>
            </Grid>
            <Grid container justifyContent='space-between' spacing={2}>
              <Grid item xs={12} md={12}>
                <Typography variant='h5' className={classes.titleDrawResults}>
                  Calculations
                </Typography>
                <Grid container spacing={4} >
                  {calculationsColumns.map(column => {
                    return getCalculationItem(column.text, column.amount, column.description, column.isPercentage);
                  })}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      {isInternal &&
        <Grid item xs={12} className={classes.buttonContainer} data-html2canvas-ignore>
          <Grid container justifyContent='center' spacing={2}>
            <Grid item xs={6} md={2}>
              <Button
                onClick={handleDecline}
                className={classes.declineButton}
                size='large'
                variant='outlined'
                fullWidth
              >
                DECLINE
              </Button>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button
                onClick={handleSave}
                className={classes.saveButton}
                size='large'
                variant='outlined'
                fullWidth
              >
                SAVE
              </Button>
            </Grid>
            <Grid item xs={6} md={2}>
              <Button
                className={classes.approveButton}
                disabled={!store.isFilled}
                variant='contained'
                size='large'
                fullWidth
                onClick={handleApprove}
              >
                APPROVE
              </Button>
            </Grid>
          </Grid>
        </Grid>
      }
      <Grid
        item
        container
        spacing={2}
        id='images-report'
      >
        <Grid item xs={12} md={12}>
          <Paper className={classes.paper} borderType={PaperBorderType.NO_BORDER}>
            <Grid container spacing={2} className={classes.headerGrid} >
              <Grid item xs={12} md={12}>
                <Typography variant='h4' className={classes.topHeaderSection}>PROPERTY IMAGES</Typography>
              </Grid>
              <Grid item container spacing={2} >
                {
                  store?.pictures?.length ? (
                    store.pictures.map((picture, index) => {

                      if (displayInspectionPhotos) {
                        if (picture.reported) return null;
                        return (
                          <Grid key={index} item xs={12} md={4} lg={3} style={{ position: "relative" }}>
                            <PhotoCover
                              url={picture.image}
                              rotationDegrees={picture.preferredRotation}
                              caption={picture.caption}
                              onClick={() => {
                                setShowPicturesModal(true);
                                setSelectedPhotoKey(picture.inspectionPhotoId);
                              }}
                            />
                            <IconButton
                              onClick={() => {
                                setShowPicturesModal(true);
                                setSelectedPhotoKey(picture.inspectionPhotoId);
                              }} style={{
                                position: "absolute",
                                top: 10,
                                right: 10
                              }}>
                              <CropFree />
                            </IconButton>
                          </Grid>
                        );
                      }

                      return (
                        <Grid key={index} item xs={12} sm={6} md={4}>
                          <ImageCarouselViewer
                            key={picture.image}
                            title={picture.title}
                            subTitle={picture.consumer_note}
                            image={picture}
                            showTitle
                            store={store}
                          />
                        </Grid>
                      );
                    })
                  ) : (
                    <Grid item spacing={2}>
                      <Typography variant="body1">
                        {arePicturesLoading ? (
                          <Box display="flex" alignItems="center">
                            <CircularProgress color="primary" />
                            <Box pl={2}>Loading photos...</Box>
                          </Box>
                        ) : (
                          <>There are no photos.</>
                        )}
                      </Typography>
                    </Grid>
                  )}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <PhotoGalleryModal title="Photos" open={showPicturesModal} enableZoom onClose={() => setShowPicturesModal(false)} photos={inspectionPhotos} onPhotoRotation={onPhotoRotationHandler} selectedPhotoKey={selectedPhotoKey} />
      </Grid>
    </Grid >
  );
});

const DownloadBtn = ({ loading, loadingTooltip, text, onClick }) => {
  const classes = useStyles();
  return loading ? (
    <Tooltip title={loadingTooltip}>
      <div className={classes.downloadContainer} style={{ alignItems: 'center', opacity: 0.4 }}>
        <GetApp className={classes.icon} />
        <p className={classes.buttonLabel}>{text}</p>
      </div>
    </Tooltip>
  ) : (
    <div className={classes.downloadContainer} onClick={onClick}>
      <GetApp className={classes.icon} />
      <p className={classes.buttonLabel}>{text}</p>
    </div>
  );
};

const rotateImage = (imageUrl, degrees) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const rotation = (degrees * Math.PI) / 180; // In rads
      const canvas = document.createElement('canvas');

      if (degrees === 90 || degrees === 270) {
        canvas.width = img.naturalHeight;
        canvas.height = img.naturalWidth;
      } else {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return reject('Canvas context not available');

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rotation);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);

      canvas.toBlob(resolve);
    };
    img.onerror = reject;
    img.src = imageUrl;
  });
};

const PhotoCover = ({
  url,
  rotationDegrees,
  onClick,
  caption,
}) => {
  const classes = useStyles();
  const imgRef = useRef();
  const [rotatedUrl, setRotatedUrl] = useState<string>();


  useEffect(() => {
    if (rotationDegrees) {
      rotateImage(url, rotationDegrees).then((blob: Blob) => setRotatedUrl(URL.createObjectURL(blob)))
    } else {
      setRotatedUrl(url);
    }
  }, [imgRef.current]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
      }}
      onClick={onClick}
    >
      {rotatedUrl && (
        <img ref={imgRef} src={rotatedUrl} data-originalURL={url} data-rotation={rotationDegrees} width="100%" />
      )}
      {caption && (
        <Box
          borderRadius={'16px'}
          m={0.5}
          py={0.5}
          px={1.5}
          position={'absolute'}
          left={0}
          bottom={0}
          width={'calc(100% - 8px)'}
          bgcolor={'rgba(0,0,0, 0.5)'}
          color={'white'}
        >
          {caption}
        </Box>
      )}
    </div>
  );
};
